import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import {
  Tile,
  Button,
  Tag,
  InlineNotification,
  Form,
  TextInput,
  PasswordInput,
  Stack,
} from '@carbon/react';
import { useAuth } from '@/contexts/AuthContext';
import { getClerkErrorMessage } from '@/lib/clerkErrors';
import { ALLOWED_EMAIL_DOMAIN, isAllowedEmail } from '@/lib/emailPolicy';
import { AuthInfoTile, ClerkMissingNotice } from '@/components/auth/AuthShell';
import VerificationCodeInput from '@/components/VerificationCodeInput';

const LoginPageContent = () => {
  const location = useLocation();
  const isSignUp = location.pathname.startsWith('/sign-up');
  const navigate = useNavigate();

  const { isLoaded: signInLoaded, signIn, setActive: setSignInActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setSignUpActive } = useSignUp();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationFlow, setVerificationFlow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isReady =
    verificationFlow === 'sign-up'
      ? signUpLoaded
      : verificationFlow === 'sign-in' ||
          verificationFlow === 'sign-in-first' ||
          verificationFlow === 'client-trust'
        ? signInLoaded
        : isSignUp
          ? signUpLoaded
          : signInLoaded;

  const findEmailCodeSecondFactor = () =>
    signIn?.supportedSecondFactors?.find((factor) => factor.strategy === 'email_code');

  const findEmailCodeFirstFactor = () =>
    signIn?.supportedFirstFactors?.find((factor) => factor.strategy === 'email_code');

  const prepareEmailSecondFactor = async () => {
    const emailCodeFactor = findEmailCodeSecondFactor();
    if (!emailCodeFactor) return false;

    await signIn.prepareSecondFactor({
      strategy: 'email_code',
      emailAddressId: emailCodeFactor.emailAddressId,
    });
    return true;
  };

  const resetVerification = () => {
    setPendingVerification(false);
    setVerificationFlow(null);
    setVerificationCode('');
    setError('');
  };

  const switchMode = (signUpMode) => {
    resetVerification();
    navigate(signUpMode ? '/sign-up' : '/login', { replace: true });
  };

  const startVerification = (flow) => {
    setVerificationFlow(flow);
    setPendingVerification(true);
    setVerificationCode('');
    setError('');
  };

  const completeSignIn = async () => {
    if (signIn.status !== 'complete') {
      setError('Sign in could not be completed. Please try again.');
      return;
    }
    await setSignInActive({ session: signIn.createdSessionId });
    navigate('/dashboard', { replace: true });
  };

  const completeSignUp = async () => {
    if (signUp.status === 'complete') {
      await setSignUpActive({ session: signUp.createdSessionId });
      navigate('/dashboard', { replace: true });
      return;
    }

    if (signUp.status === 'missing_requirements') {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      startVerification('sign-up');
      return;
    }

    setError('Sign up could not be completed. Please try again.');
  };

  const handleSignInSecondFactor = async () => {
    if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
      if (await prepareEmailSecondFactor()) {
        startVerification(signIn.status === 'needs_client_trust' ? 'client-trust' : 'sign-in');
        return;
      }
    }

    if (signIn.status === 'needs_first_factor') {
      const emailCodeFactor = findEmailCodeFirstFactor();

      if (emailCodeFactor) {
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: emailCodeFactor.emailAddressId,
        });
        startVerification('sign-in-first');
        return;
      }
    }

    if (signIn.supportedSecondFactors?.some((factor) => factor.strategy === 'totp')) {
      setError(
        'This account uses an authenticator app for sign-in. Use Clerk dashboard settings or contact support.'
      );
      return;
    }

    if (signIn.status === 'needs_client_trust' || signIn.status === 'needs_second_factor') {
      setError('We could not send a verification code. Check that your email is verified on this account.');
      return;
    }

    setError('Additional verification is required for this account.');
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    if (!signInLoaded) return;

    if (!isAllowedEmail(email)) {
      setError(`Only @${ALLOWED_EMAIL_DOMAIN} accounts can sign in.`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await signIn.create({ identifier: email.trim(), password });

      if (signIn.status === 'complete') {
        await completeSignIn();
        return;
      }

      await handleSignInSecondFactor();
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    if (!signUpLoaded) return;

    if (!isAllowedEmail(email)) {
      setError(`Only @${ALLOWED_EMAIL_DOMAIN} accounts can create an account.`);
      return;
    }

    setSubmitting(true);
    setError('');

    const trimmedName = name.trim();
    const [firstName, ...rest] = trimmedName.split(/\s+/);
    const lastName = rest.join(' ');

    try {
      await signUp.create({
        emailAddress: email.trim(),
        password,
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
      });
      await completeSignUp();
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();

    if (verificationCode.length !== 6) {
      setError('Enter the full 6-digit code from your email.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (verificationFlow === 'sign-up') {
        if (!signUpLoaded || !signUp) return;
        await signUp.attemptEmailAddressVerification({ code: verificationCode });
        await completeSignUp();
        return;
      }

      if (verificationFlow === 'sign-in' || verificationFlow === 'client-trust') {
        if (!signInLoaded || !signIn) return;
        await signIn.attemptSecondFactor({ strategy: 'email_code', code: verificationCode });
        await completeSignIn();
        return;
      }

      if (verificationFlow === 'sign-in-first') {
        if (!signInLoaded || !signIn) return;
        await signIn.attemptFirstFactor({ strategy: 'email_code', code: verificationCode });

        if (signIn.status === 'complete') {
          await completeSignIn();
          return;
        }

        await handleSignInSecondFactor();
      }
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setSubmitting(true);
    setError('');

    try {
      if (verificationFlow === 'sign-up') {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      } else if (verificationFlow === 'sign-in' || verificationFlow === 'client-trust') {
        await prepareEmailSecondFactor();
      } else if (verificationFlow === 'sign-in-first') {
        const emailCodeFactor = signIn.supportedFirstFactors?.find(
          (factor) => factor.strategy === 'email_code'
        );
        if (emailCodeFactor) {
          await signIn.prepareFirstFactor({
            strategy: 'email_code',
            emailAddressId: emailCodeFactor.emailAddressId,
          });
        }
      }
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="auth-section"
      data-testid={isSignUp ? 'sign-up-page' : 'login-page'}
    >
      <div className="section-header">
        <Tag type="blue">ACCOUNT</Tag>
        <h2 className="section-title">
          {pendingVerification
            ? verificationFlow === 'client-trust'
              ? 'Verify this device'
              : 'Verify your email'
            : isSignUp
              ? 'Create your SenseStack account'
              : 'Sign in to SenseStack'}
        </h2>
        {!isSignUp && !pendingVerification && (
          <p className="section-description">
            Access contracts, invoices, and time tracking from your dashboard.
          </p>
        )}
        {pendingVerification && (
          <p className="section-description">
            {verificationFlow === 'client-trust'
              ? <>We sent a 6-digit code to <strong>{email}</strong> to confirm this sign-in.</>
              : <>We sent a 6-digit code to <strong>{email}</strong>.</>}
          </p>
        )}
      </div>

      <div className="auth-layout">
        <AuthInfoTile />

        <Tile className="auth-form-tile">
          {error && (
            <InlineNotification
              kind="error"
              title="Unable to continue"
              subtitle={error}
              hideCloseButton
              className="auth-form-error"
            />
          )}

          {pendingVerification ? (
            <Form className="auth-form" onSubmit={handleVerifyCode}>
              <Stack gap={6}>
                <VerificationCodeInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                />
                <Button
                  type="submit"
                  disabled={!isReady || submitting || verificationCode.length !== 6}
                >
                  {submitting ? 'Verifying…' : 'Verify code'}
                </Button>
                <Button
                  kind="tertiary"
                  type="button"
                  disabled={submitting || !isReady}
                  onClick={handleResendCode}
                >
                  Resend code
                </Button>
                <Button
                  kind="ghost"
                  type="button"
                  disabled={submitting}
                  onClick={resetVerification}
                >
                  {verificationFlow === 'sign-up' ? 'Back to sign up' : 'Back to sign in'}
                </Button>
              </Stack>
            </Form>
          ) : isSignUp ? (
            <Form className="auth-form" onSubmit={handleSignUp}>
              <Stack gap={6}>
                <TextInput
                  id="signup-name"
                  labelText="Full name"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                />
                <TextInput
                  id="signup-email"
                  labelText="Email"
                  type="email"
                  placeholder="you@sensestack.com.au"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
                <PasswordInput
                  id="signup-password"
                  labelText="Password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <Button type="submit" disabled={!isReady || submitting}>
                  {submitting ? 'Creating account…' : 'Create account'}
                </Button>
                <p className="auth-form-switch">
                  Already have an account?{' '}
                  <button type="button" className="auth-form-link" onClick={() => switchMode(false)}>
                    Sign in
                  </button>
                </p>
              </Stack>
            </Form>
          ) : (
            <Form className="auth-form" onSubmit={handleSignIn}>
              <Stack gap={6}>
                <TextInput
                  id="login-email"
                  labelText="Email"
                  type="email"
                  placeholder="you@sensestack.com.au"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
                <PasswordInput
                  id="login-password"
                  labelText="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
                <p className="auth-form-forgot">
                  <Link to="/forgot-password" className="auth-form-link">
                    Forgot password?
                  </Link>
                </p>
                <Button type="submit" disabled={!isReady || submitting}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
                <p className="auth-form-switch">
                  Don&apos;t have an account?{' '}
                  <button type="button" className="auth-form-link" onClick={() => switchMode(true)}>
                    Sign up
                  </button>
                </p>
              </Stack>
            </Form>
          )}
        </Tile>
      </div>
    </section>
  );
};

const LoginPage = () => {
  const { clerkMissing } = useAuth();
  if (clerkMissing) return <ClerkMissingNotice />;
  return <LoginPageContent />;
};

export default LoginPage;
